import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import L from "leaflet";
import { MapContainer, Marker } from "react-leaflet";
import { ChevronLeft, ChevronRight, Pencil } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { TopBar } from "./kit";
import { TAP_LINK_STYLE } from "./kit/tapTarget";
import { useChromelessOverride } from "../context/ChromelessOverrideContext";
import { useRetireTextSize } from "../context/RetireTextSizeContext";
import { AccessibilityMenu } from "./AccessibilityMenu";
import { AccountMenu } from "./AccountMenu";
import { NotificationsBell } from "./NotificationsBell";
import { Basemaps, FacilityList } from "../pages/GroundsMap";
import MapControlPill from "./kit/MapControlPill";
import MapFullScreen from "./kit/MapFullScreen";
import MapFullScreenSync from "./kit/MapFullScreenSync";
import ConditionUpdate from "./ConditionUpdate";
import GroundEditPanel from "./GroundEditPanel";
import { PinDropCatcher, SurfacePinHint, SurfacePinLayer, useSurfacePinDraft } from "./SurfacePinMap";
import {
  GROUND_PIN_FROM_MAP,
  canOfferGroundEdit,
  groundPin,
  isGroundPin,
  isPrivateGround,
} from "../lib/groundEdit";
import { canUpdateCondition } from "../lib/conditionUpdate";
import { canAddFacility, upsertFacilityRow } from "../lib/facilityAdd";
import { resolveGroundById } from "../lib/directoryLoad";
import {
  COACH_CARD_NOTE,
  COACH_CARD_NOTE_EDIT,
  DIRECTORY_FAILED_BODY,
  DIRECTORY_FAILED_TITLE,
  coachFactsLine,
} from "../lib/groundsNearYou";
import { homeLabelForRole, homePathForRole } from "../lib/homePath";
import { useGroundRatings } from "./FacilityRatings";
import { withCreate } from "../lib/activityCreate";
import { markNav, track } from "../lib/usageTracker";
import { NOT_RECORDED } from "../lib/counters";
import {
  FACTS_LOAD_FAILED,
  RECORD_LOAD_FAILED,
  groundBattingRecord,
  groundFactTiles,
  groundFigures,
  groundSurfaceLabel,
  playedHereLabel,
  rateSurfaceHelperText,
} from "../lib/groundCard";
import { DETAIL_DEFAULT_BASEMAP, readBasemap, resolveBasemap, writeBasemap } from "../lib/basemap";

/** D167 G10 — the same small pin `GroundsMap.jsx`'s own `makeIcon` draws,
 * kept local rather than reaching into that page's module-private helper:
 * this card needs exactly one marker style (the ground itself, or the
 * correction's movable pin), never the source-coloured directory set. */
function makeCardPinIcon(color, size = 34) {
  return new L.DivIcon({
    className: "custom-marker",
    html: `<div aria-hidden="true" style="width:${size}px;height:${size}px;border-radius:50% 50% 50% 0;background:${color};border:3px solid #fff;transform:rotate(-45deg);box-shadow:0 6px 14px rgba(0,0,0,0.25);"></div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size],
    popupAnchor: [0, 4 - size],
  });
}
const CARD_PIN_ICON = makeCardPinIcon("#0B2E59");
const CARD_MOVABLE_PIN_ICON = makeCardPinIcon("#DC2626", 44);

/** A coach has no `player_id` for `/matches` to resolve, and no record to show (#1288). */
function readMatches(isCoach) {
  if (isCoach) return Promise.resolve({ ok: true, data: [] });
  return api.get("/matches").then((r) => ({ ok: true, data: r.data })).catch(() => ({ ok: false, data: [] }));
}

/** Rate the surface is a player's control; a coach's card reads no ratings (#1288). */
function ratingsGroundId(isCoach, ground) {
  return isCoach ? undefined : ground?.ground_id;
}

/**
 * Issue #766 — the ground card. Behind `newUi("ground-card")`
 * (`GroundsMap.jsx`), a full-page state on `/grounds-map` — no new route,
 * no Dialog (D167 G1, the same `useChromelessOverride` shape
 * `PlanScreen.jsx` uses) — reached from a played/nearby row with a
 * `ground_id` (D167 G3, wired in `GroundsNearYouScreen.jsx`). Players only
 * (D167 G2) — `GroundsMap.jsx`'s own gate is what keeps a coach or admin on
 * the shipped dialog; this component carries no role check of its own.
 *
 * Matches `GroundCard.dc.html` (round 5, sha256 95c7daf0…), ruled against
 * the issue text by D167 (`decisions-2026-09-24.md`) where they differ —
 * see that ruling's twelve items (G1-G12) for what each departure below is
 * answering.
 *
 * `hideIndoor` (issue #1008, D203.1) — this component's own single caller
 * (`GroundsMap.jsx`'s `newUi("ground-card")` gate above) passes it
 * explicitly, computed at that same gate, and it is forwarded verbatim to
 * `GroundEditPanel`'s "Correct details" form; see that file's own header
 * for why the flag is read there rather than inside this or that shared
 * component.
 *
 * SELF-CONTAINED DATA, DELIBERATELY NOT `GroundsMap.jsx`'s OWN `selected`/
 * `grounds`/`facilities` state: those back the SHIPPED dialog and, on the
 * flag-on leg, the unbounded `/grounds`+`/grounds/facilities` reads that
 * feed them are exactly what #643/#644 removed for a player on this route.
 * This component fetches its own three reads once per open (never per
 * render — ruling 2's own words, extended to the ground/facilities reads
 * alongside the average): the ground's own row (`resolveGroundById`,
 * `lib/directoryLoad.js` — PR #921 review F1: the unbounded `GET /grounds`
 * read this used to scan is capped at 1,000 rows ordered by id, so a
 * ground past that cap "did not exist" to a `.find()` over it; `hint`
 * (the coordinate and name the played/nearby row already carries, threaded
 * down from `GroundsMap.jsx`) lets this resolve by a tight bbox or a name
 * search instead, neither bounded by the directory's SIZE), its
 * facilities (`GET /grounds/:id/facilities`, the same call
 * `useGroundsNearYou.js`'s `ensureFacilities` makes), and the caller's own
 * matches (`GET /matches`, ruling 2) — filtered to this ground by
 * `lib/groundCard.js`'s pure derivations, never recomputed on a keystroke
 * or a re-render.
 *
 * `savedIds`/`onToggleSaved` ARE threaded down from `GroundsMap.jsx`
 * (G11) — that pair is populated by `loadSaved`, which runs unconditionally
 * on this page regardless of which branch renders, and reusing it (rather
 * than a second `GET /players/me/grounds`) is what keeps the directory's
 * own saved badge and this card's save button from ever disagreeing.
 *
 * PR #921 review (F2a) — a FAILED facilities or matches read is never
 * folded into an empty array. That used to make the card state a false
 * fact by omission ("Fields 0 · Nets 0", "Not played here yet" over a
 * read that simply never answered) — D55's own rule, restated for a
 * derived fact rather than a form field: a failure is admitted, in ink,
 * never guessed at as a zero.
 */
export default function GroundCard({ groundId, hint, savedIds, onToggleSaved, onBack, hideIndoor = false, arrivalFor = null, onArrivalConsumed = null, embedded }) {
  const { user } = useAuth();
  // #1288 (round-12 CoachGrounds): a coach logs no matches, so the card drops
  // the player's record, Rate the surface, Add a field or net, Log a match
  // here and Save (D301 Q4). The player's card is unchanged.
  const role = user?.role;
  const isCoach = role === "coach";
  const { setActive: setChromeless } = useChromelessOverride();
  const { setRetired: setTextSizeRetired } = useRetireTextSize();
  useEffect(() => {
    // #1430: embedded in the detail column under the map, the list screen beside it already owns the page.
    if (embedded) return undefined;
    setChromeless(true);
    setTextSizeRetired(true);
    return () => {
      setChromeless(false);
      setTextSizeRetired(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [ground, setGround] = useState(null);
  const [facilities, setFacilities] = useState([]);
  const [facilitiesFailed, setFacilitiesFailed] = useState(false);
  const [matches, setMatches] = useState([]);
  const [matchesFailed, setMatchesFailed] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const load = useMemo(
    () => async () => {
      setLoadFailed(false);
      setFacilitiesFailed(false);
      setMatchesFailed(false);
      try {
        // F2a — each of the three reads answers for ITSELF: a failed
        // facilities or matches read must never fall back to `[]`, which
        // would be indistinguishable from "this ground genuinely has
        // none" / "you have never played here" (both false claims). Only
        // the GROUND row's own failure takes the whole card down — there
        // is nothing honest left to show without it.
        const [row, facilityResult, matchResult] = await Promise.all([
          resolveGroundById(api, groundId, hint || {}),
          api.get(`/grounds/${groundId}/facilities`).then((r) => ({ ok: true, data: r.data })).catch(() => ({ ok: false, data: [] })),
          readMatches(isCoach),
        ]);
        if (!row) {
          setLoadFailed(true);
        } else {
          setGround(row);
          setFacilities(facilityResult.data || []);
          setFacilitiesFailed(!facilityResult.ok);
          setMatches(matchResult.data || []);
          setMatchesFailed(!matchResult.ok);
        }
      } catch {
        setLoadFailed(true);
      } finally {
        setLoaded(true);
      }
    },
    [groundId, hint, isCoach],
  );

  // Fetch ONCE per open (ruling 2) — re-fires only when a DIFFERENT ground
  // id is opened, never on a render this component's own state causes.
  useEffect(() => {
    setLoaded(false);
    setGround(null);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groundId]);

  const [correcting, setCorrecting] = useState(false);
  const [correctionPin, setCorrectionPin] = useState(null);
  const [showFacilityPanel, setShowFacilityPanel] = useState(false);
  const surfaceDraft = useSurfacePinDraft();
  const [basemap, setBasemap] = useState(readBasemap);
  const chooseBasemap = (id) => {
    setBasemap(id);
    writeBasemap(id);
  };

  const startCorrecting = () => {
    // Defence in depth (#1074 review F1): never leave a surface draft open
    // behind the correction form.
    surfaceDraft.close();
    setArrivalAdd(null);
    setCorrecting(true);
    setCorrectionPin(groundPin(ground));
  };
  const stopCorrecting = () => {
    setCorrecting(false);
    setCorrectionPin(null);
  };
  // PR #921 review (F6) — no toast on either write. Ruling 8 (D109): the
  // card ITSELF is the confirmation — the corrected name/pin/type reads
  // straight off `ground` the instant this state updates, and the new
  // facility appears in the list it just changed. A toast would say the
  // same thing twice.
  const handleGroundCorrected = (g) => {
    setGround((prev) => (prev ? { ...prev, ...g } : prev));
    stopCorrecting();
  };
  const handleFacilityUpdated = (f) => {
    // #1075 review F2: the surface is saved; the arrival is spent. FacilityList
    // swaps branch on the first row and mounts a fresh form, which would
    // otherwise auto-open again with the old pin.
    setArrivalAdd(null);
    setFacilities((prev) => upsertFacilityRow(prev, f));
  };

  const facts = useMemo(() => groundFactTiles(facilities), [facilities]);
  const surfaceLabel = useMemo(() => groundSurfaceLabel(facilities), [facilities]);
  const record = useMemo(() => groundBattingRecord(matches, groundId), [matches, groundId]);
  const figures = useMemo(() => groundFigures(matches, groundId), [matches, groundId]);
  // F2d — "Rate the surface" states the SAME eligibility the shipped
  // per-facility rating control itself enforces (`can_rate`,
  // `api/src/lib/facilityRatings.ts`, mirrored by `useGroundRatings`
  // below) — never "You've played here, so you can" for a ground the
  // player has not played, and never a control that would 403.
  const ratings = useGroundRatings(ratingsGroundId(isCoach, ground));

  const hasPin = Boolean(ground && ground.latitude != null && ground.longitude != null);
  const canOfferEdit = Boolean(ground && user && canOfferGroundEdit(ground, user));
  const canAdd = Boolean(ground && user && canAddFacility(ground, user));
  const saved = Boolean(ground && savedIds && savedIds.has(ground.ground_id));
  // One count per card opened (a load that found the ground), never its name or id.
  const cardShown = Boolean(loaded && !loadFailed && ground);
  useEffect(() => {
    if (cardShown) track("grounds.card_open");
  }, [cardShown, groundId]);

  // #1075 — arriving from the duplicate prompt's "Add a field or net to X
  // instead" (#547): the add form opens when eligible. An ineligible arrival
  // gets the card's own refusal under "Add a field or net" (one message, not
  // two). The rules live on the host's hook; this card only asks.
  const arrival = arrivalFor ? arrivalFor(ground, correcting) : { note: false, autoAdd: null };
  // Applied ONCE, then held here: `arrival` flips back to null while
  // Correct details is open and returns after it, and a re-derived value
  // would re-open a panel (and re-place the carried pin) the player had
  // since collapsed (#1074 review's stray-pin traps).
  const [arrivalAdd, setArrivalAdd] = useState(null);
  const arrivalApplied = useRef(false);
  const arrivingAdd = arrival.autoAdd;
  useEffect(() => {
    if (!arrivingAdd || arrivalApplied.current) return;
    arrivalApplied.current = true;
    setArrivalAdd(arrivingAdd);
    setShowFacilityPanel(true);
  }, [arrivingAdd]);
  const leave = () => {
    markNav("back");
    if (onArrivalConsumed) onArrivalConsumed();
    onBack();
  };

  const addRefusalText = () => {
    if (!ground) return "";
    if (!isPrivateGround(ground)) {
      // D167 G7 — only the drawn FIRST sentence: the rest is the drawing
      // talking to Martin (players never see issue numbers, D120).
      return "This ground came from the world directory, so only an administrator can.";
    }
    // "Other refusals use the gate's own reason" (G7) — a private ground
    // this caller did not create, or a merged record: no drawn sentence
    // exists for either, so the honest, generic answer stands in.
    return "Only its record keeper or an admin can add fields and nets here.";
  };

  if (!loaded) {
    return (
      <div data-testid="ground-card" style={{ color: "var(--nw-ink)" }}>
        <GroundCardTopBar role={role} embedded={embedded} />
        {/* #1125: a slow or stuck load still has a way out. */}
        <div className="flex flex-col" style={{ gap: 10, padding: 20 }}>
          {isCoach ? <CoachBack onClick={leave} /> : (
          <button
            type="button"
            data-testid="ground-card-back"
            onClick={leave}
            style={{ alignSelf: "flex-start", display: "inline-flex", alignItems: "center", justifyContent: "flex-start", minHeight: 44, minWidth: 44, background: "none", border: "none", padding: 0, cursor: "pointer", fontSize: 12, lineHeight: 1.5, color: "var(--nw-ink-muted)", textDecoration: "underline" }}
          >
            Back
          </button>
          )}
          <div className="nw-help">Loading…</div>
        </div>
      </div>
    );
  }

  if (loadFailed || !ground) {
    return (
      <div data-testid="ground-card" style={{ color: "var(--nw-ink)" }}>
        <GroundCardTopBar role={role} embedded={embedded} />
        <div className="flex flex-col" style={{ gap: 10, padding: 20 }}>
          {isCoach ? <CoachBack onClick={leave} /> : (
          <button
            type="button"
            data-testid="ground-card-back"
            onClick={leave}
            style={PLAYER_BACK_STYLE}
          >
            Back
          </button>
          )}
          <FailureNote testId="ground-card-failed" retryTestId="ground-card-retry" title={DIRECTORY_FAILED_TITLE} body={DIRECTORY_FAILED_BODY} onRetry={load} />
        </div>
      </div>
    );
  }

  const townLine = [ground.city, ground.country].filter(Boolean).join(" · ");

  return (
    <div data-testid="ground-card" style={{ color: "var(--nw-ink)" }}>
      <GroundCardTopBar role={role} embedded={embedded} />

      <div data-testid="ground-card-photo" style={{ position: "relative", height: 140, background: "#E2E8F0", borderBottom: "1px solid #CBD5E1", overflow: "hidden" }}>
        {hasPin ? (
          // #1669 (D449): full screen on top of the pill, as `LogGroundMapBand` does it. The inline map keeps its 140px; only
          // the overlay is full screen. The same map moves, so a correction or surface pin placed there is placed here.
          <MapFullScreen testId="ground-card-map" controlInPill height="100%" frame={false}>
            {() => (
              <>
                <MapContainer
                  key={`ground-card-map-${ground.ground_id}`}
                  center={[ground.latitude, ground.longitude]}
                  zoom={16}
                  style={{ height: "100%", width: "100%" }}
                  scrollWheelZoom={false}
                  zoomControl={false}
                >
                  {/* The one map control, a pill: full screen, then map/satellite. No +/- box, no Street/Satellite panel. */}
                  <Basemaps active={resolveBasemap(basemap, DETAIL_DEFAULT_BASEMAP)} onChoose={chooseBasemap} wrap pill />
                  <MapControlPill basemap={resolveBasemap(basemap, DETAIL_DEFAULT_BASEMAP)} onBasemap={chooseBasemap} />
                  <MapFullScreenSync />
                  {correcting && <PinDropCatcher onDrop={(pos) => setCorrectionPin({ ...pos, via: GROUND_PIN_FROM_MAP })} />}
                  {/* #548: while the ADD-SURFACE form is open instead, the same taps
                      place the surface's pin. Never both. */}
                  {!correcting && showFacilityPanel && surfaceDraft.active && <SurfacePinLayer pin={surfaceDraft.pin} onMove={surfaceDraft.setPin} />}
                  <Marker
                    // #1176: the pin is focusable, so it carries the ground's name.
                    // Re-keyed on `correcting` because leaflet reads `title` once.
                    key={correcting ? "pin-move" : "pin-view"}
                    // View mode: the pin does nothing, so it is not a Tab stop.
                    keyboard={correcting}
                    title={correcting ? `Move pin: ${ground.name}` : ground.name}
                    position={correcting && isGroundPin(correctionPin) ? [correctionPin.lat, correctionPin.lng] : [ground.latitude, ground.longitude]}
                    icon={correcting ? CARD_MOVABLE_PIN_ICON : CARD_PIN_ICON}
                    draggable={correcting}
                    eventHandlers={correcting ? {
                      dragend: (e) => {
                        const moved = e.target.getLatLng();
                        setCorrectionPin({ lat: moved.lat, lng: moved.lng, via: GROUND_PIN_FROM_MAP });
                      },
                    } : undefined}
                  />
                </MapContainer>
                {/* PR #921 (F5): the hint the shipped dialog's map wears while a correction is open, named on the map
                    itself as well as in the form. `pointer-events-none` so it never eats the tap it describes. */}
                {correcting && (
                  <div
                    className="pointer-events-none absolute inset-x-0 top-0 z-[1000] flex justify-center p-2"
                    data-testid="ground-edit-pin-hint"
                    role="status"
                  >
                    <p className="rounded-full bg-slate-900/85 px-3 py-1 text-[11px] font-semibold text-white shadow">
                      Drag the pin, or tap the map, to move it
                    </p>
                  </div>
                )}
              </>
            )}
          </MapFullScreen>
        ) : null}
        {!hasPin && (
          // D167 G10 — the drawing's own `no-pin` state, verbatim (S34, D83).
          <div data-dc-state="no-pin" className="flex items-center justify-center" style={{ height: "100%", padding: "0 28px", textAlign: "center" }}>
            <span style={{ fontSize: 12, lineHeight: 1.5, color: "#475569" }}>No map: this ground has no pin yet.</span>
          </div>
        )}
      </div>

      <div className="flex flex-col" style={{ gap: 14, padding: 20 }}>
        {isCoach ? <CoachBack onClick={leave} /> : (
        <button
          type="button"
          data-testid="ground-card-back"
          onClick={leave}
          style={PLAYER_BACK_STYLE}
        >
          Back
        </button>
        )}

        <div className="flex flex-col" style={{ gap: 4 }}>
          <div data-testid="ground-card-name" style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800, fontSize: 24, letterSpacing: "-0.5px", lineHeight: 1.15, color: "var(--nw-ink)" }}>
            {ground.name}
          </div>
          {townLine && <div style={{ fontSize: 13, color: "var(--nw-ink-muted)" }}>{townLine}</div>}
        </div>

        {isCoach && (
          <CoachGroundBody
            ground={ground}
            facilities={facilities}
            facilitiesFailed={facilitiesFailed}
            onRetry={load}
            canEditCondition={canUpdateCondition(ground, user) && !correcting}
            canOfferEdit={canOfferEdit}
            onConditionUpdated={handleGroundCorrected}
          />
        )}

        {!isCoach && (
          <>
        {facilitiesFailed ? (
          // F2a — a failed facilities read is never zero-filled into
          // "Fields 0 · Nets 0"; that would state a false fact about the
          // ground, not merely an unknown one. Ruling 8 (D55): the
          // failure stays on the page, in ink, with a real retry.
          <FailureNote testId="ground-card-facts-failed" retryTestId="ground-card-facts-retry" title={FACTS_LOAD_FAILED} onRetry={load} />
        ) : (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 10 }}>
              <FactTile testId="ground-card-facts" label="Fields" value={String(facts.fields)} big />
              <FactTile label="Nets" value={String(facts.nets)} big />
              <FactTile label="Indoor" value={facts.indoor ? "Yes" : "No"} />
              <FactTile label="Floodlights" value={facts.floodlights ? "Yes" : "No"} />
            </div>

            <div className="flex items-center justify-between" style={{ gap: 12, padding: "12px 14px", background: "var(--nw-surface)", border: "1px solid var(--nw-line)", borderRadius: "var(--nw-radius-card)" }}>
              <div className="flex flex-col" style={{ gap: 2 }}>
                <div data-testid="ground-card-surface" style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--nw-ink-muted)" }}>
                  Surface
                </div>
                <div style={{ fontWeight: 600, fontSize: 15, color: "var(--nw-ink)" }}>{surfaceLabel || NOT_RECORDED}</div>
              </div>
              <ConditionUpdate
                target={{ groundId: ground.ground_id }}
                condition={ground.condition}
                ratedAt={ground.condition_rated_at}
                canEdit={canUpdateCondition(ground, user) && !correcting}
                label="Condition"
                testId={`ground-condition-${ground.ground_id}`}
                onUpdated={handleGroundCorrected}
              />
            </div>
          </>
        )}

        <div className="flex flex-col" style={{ gap: 10, padding: 14, background: "var(--nw-surface)", border: "1px solid var(--nw-line)", borderRadius: "var(--nw-radius-card)" }}>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--nw-ink-muted)" }}>
            Your record here
          </div>
          {matchesFailed ? (
            // F2a — same rule: a failed matches read must not show as
            // "Not played here yet" (a false claim) or a real average
            // computed over an incomplete answer.
            <FailureNote testId="ground-card-record-failed" retryTestId="ground-card-record-retry" title={RECORD_LOAD_FAILED} onRetry={load} />
          ) : (
            <>
              {/* F2b — the SAME count `GET /venues/played` states for this
                  ground on the list this card was opened from — every
                  match, batting or not — never the batting-only count.
                  F2c — the zero case is this card's OWN sentence
                  (undrawn), never `playedAtLabel(0)`'s "Saved · not played
                  yet", which is a different screen's claim about a
                  bookmark. */}
              <div data-testid="ground-card-played" style={{ fontSize: 15, fontWeight: 600, color: "var(--nw-ink)" }}>
                {playedHereLabel(record.matchCount, record.lastPlayed)}
              </div>
              {/* #1291 (D214 L1) — Matches, Runs, Wickets, Economy. No tiles
                  for a ground never played: four dashes would read as four
                  figures. */}
              {record.matchCount > 0 && (
                <div data-testid="ground-card-figures" style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 10 }}>
                  <FactTile testId="ground-card-figure-matches" label="Matches" value={String(figures.matchCount)} big />
                  <FactTile testId="ground-card-figure-runs" label="Runs" value={figures.runs ?? "—"} big />
                  <FactTile testId="ground-card-figure-wickets" label="Wickets" value={figures.wickets ?? "—"} big />
                  <FactTile
                    testId="ground-card-figure-economy"
                    label="Economy"
                    value={figures.economyText}
                    big
                    note={figures.restsOn}
                    noteTestId="ground-card-figure-economy-rests"
                  />
                </div>
              )}
              {record.innings > 0 && (
                <div className="flex flex-col" style={{ gap: 4, padding: "12px 14px", background: "var(--nw-surface)", border: "1px solid var(--nw-line)", borderRadius: "10px" }}>
                  <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--nw-ink-muted)" }}>
                    Your average here
                  </div>
                  <div data-testid="ground-card-average" style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800, fontSize: 22, color: "var(--nw-ink)" }}>
                    {record.averageText}
                  </div>
                  {/* D167 G6 — the not-out sentence is conditional: it
                      addresses the shape where there is NOTHING to divide
                      by because every innings here was not out, never
                      shown beside a real average (the drawing's default
                      capture shows both at once; D167 covers that segment
                      as missing here). */}
                  {record.averageValue != null ? (
                    <div style={{ fontSize: 12, lineHeight: 1.5, color: "var(--nw-ink-muted)" }}>
                      From {record.inningsWords} innings at this ground.
                    </div>
                  ) : (
                    record.isNotOutEveryTime && (
                      <div style={{ fontSize: 12, lineHeight: 1.5, color: "var(--nw-ink-muted)" }}>
                        Not out every time you have batted here? Then there is no average yet — nothing to divide
                        by, and blank is not nought.
                      </div>
                    )
                  )}
                </div>
              )}
            </>
          )}
        </div>

        <button
          type="button"
          data-testid="ground-card-rate"
          onClick={() => {
            // #1074 review F5: collapsing unmounts FacilityAddForm without its
            // closeForm, so end the surface draft here.
            if (showFacilityPanel) {
              surfaceDraft.close();
              setArrivalAdd(null);
            }
            setShowFacilityPanel((v) => !v);
          }}
          aria-expanded={showFacilityPanel}
          className="flex items-center justify-between"
          style={{ minHeight: 48, padding: "0 14px", background: "var(--nw-surface)", border: "1px solid var(--nw-line)", borderRadius: 10, cursor: "pointer" }}
        >
          <div className="flex flex-col" style={{ alignItems: "flex-start" }}>
            <span style={{ fontSize: 15, fontWeight: 600, color: "var(--nw-navy)" }}>Rate the surface</span>
            {/* F2d — conditional on the SAME eligibility the rating
                control itself enforces, never the drawn "You've played
                here, so you can" unconditionally. `ratings.data` is `null`
                until the read answers (or if it fails), in which case
                nothing is claimed either way. */}
            <span style={{ fontSize: 12, color: "var(--nw-ink-muted)" }}>
              {rateSurfaceHelperText(ratings.data ? ratings.data.can_rate : null)}
            </span>
          </div>
          <ChevronRight size={20} style={{ color: "var(--nw-navy)" }} />
        </button>

        {showFacilityPanel && (
          <FacilityList
            ground={ground}
            list={facilities}
            canEdit={canAdd && !correcting}
            onFacilityUpdated={handleFacilityUpdated}
            pinDraft={hasPin ? surfaceDraft : null}
            autoAdd={arrivalAdd}
            onSurfaceFormOpen={() => {}}
          />
        )}

        <div
          className="flex items-center justify-between"
          style={{ minHeight: 48, padding: "10px 14px", background: "var(--nw-surface)", border: "1px solid var(--nw-line)", borderRadius: 10 }}
        >
          <div className="flex flex-col">
            <button
              type="button"
              data-testid="ground-card-add-field"
              onClick={canAdd ? () => setShowFacilityPanel(true) : undefined}
              disabled={!canAdd}
              style={{ ...TAP_LINK_STYLE, justifyContent: "flex-start", alignSelf: "flex-start", background: "none", border: "none", padding: 0, textAlign: "left", cursor: canAdd ? "pointer" : "default", fontSize: 15, fontWeight: 600, color: canAdd ? "var(--nw-navy)" : "#94A3B8" }}
            >
              Add a field or net
            </button>
            {!canAdd && (
              <span style={{ fontSize: 12, lineHeight: 1.45, color: "var(--nw-ink-muted)" }}>{addRefusalText()}</span>
            )}
          </div>
        </div>

        <Link
          data-testid="ground-card-log-here"
          to={withCreate("/performance", {
            groundId: ground.ground_id,
            groundName: ground.name,
            latitude: ground.latitude,
            longitude: ground.longitude,
          })}
          className="flex items-center justify-center"
          style={{ minHeight: 52, background: "#D4A017", color: "#0F172A", fontWeight: 700, fontSize: 16, borderRadius: 10, textDecoration: "none" }}
        >
          Log a match here
        </Link>

          </>
        )}

        {/* D167 G11 — kept, `save-detail-{id}`; #707's overlap is the
            DIRECTORY card's own `save-{id}` (a separate PR, not touched
            here). */}
        <div className="flex flex-wrap items-center gap-2">
          {!isCoach && (
          <button
            type="button"
            data-testid={`save-detail-${ground.ground_id}`}
            onClick={(e) => onToggleSaved(ground, e)}
            style={{
              minHeight: 44,
              padding: "0 16px",
              borderRadius: "var(--nw-radius-control)",
              fontSize: 14,
              fontWeight: 600,
              cursor: "pointer",
              background: saved ? "#0B2E59" : "var(--nw-surface)",
              color: saved ? "#FFFFFF" : "var(--nw-navy)",
              border: saved ? "none" : "1px solid var(--nw-line)",
            }}
          >
            {saved ? "Saved" : "Save to My Grounds"}
          </button>
          )}
          {/* D167 G8 — "Correct details" and `GroundEditPanel` stay, below
              the drawn content, unstyled as drawn (structure drift only). */}
          {/* #1074 review F1: as the shipped dialog (GroundsMap.jsx:3197), no
              Correct details while the add-surface form is open. */}
          {canOfferEdit && !correcting && !surfaceDraft.active && (
            <button
              type="button"
              data-testid={`edit-detail-${ground.ground_id}`}
              onClick={startCorrecting}
              className="flex items-center"
              style={{ minHeight: 44, padding: "0 16px", gap: 6, borderRadius: "var(--nw-radius-control)", fontSize: 14, fontWeight: 600, cursor: "pointer", background: "var(--nw-surface)", color: "var(--nw-navy)", border: "1px solid var(--nw-line)" }}
            >
              <Pencil size={14} /> Correct details
            </button>
          )}
        </div>
        {canOfferEdit && correcting && (
          <GroundEditPanel
            ground={ground}
            pin={correctionPin}
            onPinChange={setCorrectionPin}
            onCancel={stopCorrecting}
            onSaved={handleGroundCorrected}
            hideIndoor={hideIndoor}
          />
        )}
        {!correcting && showFacilityPanel && surfaceDraft.active && <SurfacePinHint />}
      </div>
    </div>
  );
}

/**
 * D167 — "Ground", `topbar-notifications`/`topbar-accessibility`/
 * `topbar-user`, the D140 anatomy, D159's badge and D160's tap areas —
 * the same navy `TopBar` call every other rebuilt full-page screen makes
 * (`PlanScreen.jsx`, `GroundsNearYouScreen.jsx`). No `DestinationRow`
 * (brief-766.md: "Destination row: none. The card is a step reached from
 * the grounds list.").
 */
function GroundCardTopBar({ role, embedded }) {
  // #1430: embedded in the detail column the page already has its bar, so the card draws none.
  if (embedded) return null;
  return (
    <TopBar
      variant="navy"
      homeHref={role === "coach" ? homePathForRole(role) : "/dashboard"}
      homeLabel={`CricketCompass — go to ${role === "coach" ? homeLabelForRole(role) : "your dashboard"}`}
      endControls={
        <>
          <NotificationsBell compact />
          <AccessibilityMenu hideTextSize />
          <AccountMenu empty />
        </>
      }
      data-testid="ground-card-topbar"
    />
  );
}

/**
 * #1394 (D221 G4, D354): the player's Back is a 44px tap box around the 12px underlined word. The
 * kit link rule floors the box and pulls the margin box back to one line, so the word stays
 * where it was drawn; `flex-start` keeps it at the left of the wider box.
 */
const PLAYER_BACK_STYLE = {
  ...TAP_LINK_STYLE,
  justifyContent: "flex-start",
  alignSelf: "flex-start",
  background: "none",
  border: "none",
  padding: 0,
  cursor: "pointer",
  fontSize: 12,
  lineHeight: 1.5,
  color: "var(--nw-ink-muted)",
  textDecoration: "underline",
};

/**
 * #1288 — the coach card's Back, as `CoachGrounds.dc.html` draws it: a left
 * chevron and "Back to the list", 14px/600 navy, with a 44px hit area (D221 G4).
 */
function CoachBack({ onClick }) {
  return (
    <button
      type="button"
      data-testid="ground-card-back"
      onClick={onClick}
      style={{ alignSelf: "flex-start", display: "inline-flex", alignItems: "center", gap: 6, minHeight: 44, background: "none", border: "none", padding: 0, cursor: "pointer", fontSize: 14, fontWeight: 600, color: "var(--nw-navy)" }}
    >
      <ChevronLeft size={16} strokeWidth={2} aria-hidden="true" />
      Back to the list
    </button>
  );
}

/**
 * #1288 — what a coach's ground card holds in place of the player's tiles and
 * record: "The ground" (the facts line), "Condition", and the note over Correct
 * details. A coach has logged no match here, so there are no figures of theirs.
 */
function CoachGroundBody({ ground, facilities, facilitiesFailed, onRetry, canEditCondition, canOfferEdit, onConditionUpdated }) {
  return (
    <>
      {facilitiesFailed ? (
        <>
          <FailureNote testId="ground-card-facts-failed" retryTestId="ground-card-facts-retry" title={FACTS_LOAD_FAILED} onRetry={onRetry} />
          <ConditionUpdate
            target={{ groundId: ground.ground_id }}
            condition={ground.condition}
            ratedAt={ground.condition_rated_at}
            canEdit={canEditCondition}
            label="Condition"
            testId={`ground-condition-${ground.ground_id}`}
            onUpdated={onConditionUpdated}
          />
        </>
      ) : (
        <div
          data-testid="ground-card-coach-facts"
          className="flex flex-col"
          style={{ gap: 12, padding: 14, background: "var(--nw-surface)", border: "1px solid var(--nw-line)", borderRadius: "var(--nw-radius-card)" }}
        >
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--nw-ink-muted)" }}>The ground</div>
          <div style={{ fontWeight: 400, fontSize: 15, color: "var(--nw-ink)" }}>
            {coachFactsLine({ placeType: ground.place_type, facilities })}
          </div>
          <ConditionUpdate
            target={{ groundId: ground.ground_id }}
            condition={ground.condition}
            ratedAt={ground.condition_rated_at}
            canEdit={canEditCondition}
            label="Condition"
            testId={`ground-condition-${ground.ground_id}`}
            onUpdated={onConditionUpdated}
          />
        </div>
      )}
      <p data-testid="ground-card-coach-note" className="nw-help">
        {canOfferEdit ? `${COACH_CARD_NOTE} ${COACH_CARD_NOTE_EDIT}` : COACH_CARD_NOTE}
      </p>
    </>
  );
}

/**
 * F2a — one shape for "this read failed", shared by the whole-card
 * failure (the ground row itself) and the two sectional ones (facilities,
 * matches) below it, so the three cannot drift into describing a failure
 * three different ways. `body` is optional — the sectional notes state
 * just the one line D55 asks for; the whole-card one adds the reassurance
 * ("nothing has been deleted") that only matters when the whole screen is
 * otherwise blank.
 */
function FailureNote({ testId, retryTestId, title, body, onRetry }) {
  return (
    <div
      data-testid={testId}
      className="flex flex-col"
      style={{ gap: 8, padding: "12px 14px", background: "var(--nw-surface)", border: "1px solid var(--nw-line)", borderRadius: "var(--nw-radius-card)" }}
    >
      <p className="nw-help">{title}</p>
      {body && <p className="nw-help">{body}</p>}
      <button
        type="button"
        data-testid={retryTestId}
        onClick={onRetry}
        style={{ ...TAP_LINK_STYLE, justifyContent: "flex-start", alignSelf: "flex-start", background: "none", border: "none", padding: 0, cursor: "pointer", fontSize: 14, fontWeight: 600, color: "var(--nw-navy)", textDecoration: "underline" }}
      >
        Try again
      </button>
    </div>
  );
}

function FactTile({ testId, label, value, big = false, note = null, noteTestId }) {
  return (
    <div
      data-testid={testId}
      className="flex flex-col"
      style={{ gap: 4, padding: "12px 14px", background: "var(--nw-surface)", border: "1px solid var(--nw-line)", borderRadius: 12 }}
    >
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--nw-ink-muted)" }}>
        {label}
      </div>
      <div
        style={
          big
            ? { fontFamily: "'Manrope', sans-serif", fontWeight: 800, fontSize: 22, color: "var(--nw-ink)" }
            : { fontWeight: 600, fontSize: 15, color: "var(--nw-ink)" }
        }
      >
        {value}
      </div>
      {note && (
        <div data-testid={noteTestId} style={{ fontSize: 12, lineHeight: 1.4, color: "var(--nw-ink-muted)" }}>
          {note}
        </div>
      )}
    </div>
  );
}
